import Dexie, { type Table } from 'dexie'
import type { MapAssetObject } from '~/types/map-assets.types'

const APP_DB_NAME = 'scms'
const APP_DB_VERSION = 2

type MetadataKey = 'lastFetchTime' | 'fetchedRegions' | 'filteredIds' | 'currentLicenseId'

interface Metadata {
  key: MetadataKey
  value: string
}

export class AppDB extends Dexie {
  mapAssets!: Table<MapAssetObject, number> // number = primary key type
  metadata!: Table<Metadata, string>

  constructor() {
    super(APP_DB_NAME)
    this.version(APP_DB_VERSION)
      .stores({
        mapAssets: 'id, geoHash, assetTypeId', // Primary key and index keys
        metadata: 'key'
      })
      .upgrade(async (tx) => {
        await Promise.all([tx.table('mapAssets').clear(), tx.table('metadata').clear()])
      })
  }
}

export const db = new AppDB()

export const saveMapAssets = async (assets: MapAssetObject[]): Promise<void> => {
  await db.mapAssets.bulkPut(assets)
}

export const findMapAssetsByGeoHash = async (geoHash: string): Promise<MapAssetObject[]> => {
  return db.mapAssets.where('geoHash').startsWith(geoHash).toArray()
}

export const findMapAssetsById = async (ids: number[]): Promise<MapAssetObject[]> => {
  return (await db.mapAssets.bulkGet(ids)).filter((item) => item !== undefined)
}

export const deleteMapAssetsById = async (ids: number[]): Promise<void> => {
  await db.mapAssets.bulkDelete(ids)
}

export const clearMapAssets = async (): Promise<void> => {
  await db.mapAssets.clear()
}

export const clearMetadata = async (): Promise<void> => {
  await db.metadata.clear()
}

export const setMetadata = async (key: MetadataKey, value: string): Promise<void> => {
  await db.metadata.put({ key, value })
}

export const getMetadata = async (key: MetadataKey): Promise<string | null> => {
  const entry = await db.metadata.get(key)
  return entry ? entry.value : null
}

export const getAllMapAssetIds = async (): Promise<number[]> => {
  const assets = await db.mapAssets.toArray()
  return assets.map((asset) => asset.id)
}

export const getAllMapAssets = async (): Promise<MapAssetObject[]> => {
  return db.mapAssets.toArray()
}

export const getMapAssetIdsByAssetTypes = async (assetTypeIds: number[]): Promise<number[]> => {
  if (!assetTypeIds.length) {
    return []
  }

  try {
    const assets = await db.mapAssets.where('assetTypeId').anyOf(assetTypeIds).toArray()
    return assets.map((asset) => asset.id)
  } catch (error) {
    console.error('Error querying assets by type IDs:', error, 'IDs:', assetTypeIds)
    return []
  }
}
